// M09 多項式、餘式及因式定理 - 主題 01-10 數據文件
// Polynomials, Remainder and Factor Theorem - Topics 01-10
// 系統化設計，準備後續 Supabase 數據遷移

// M09-01: 聯立方程 (Simultaneous Equations)
export const M09_TOPICS_01_TO_10 = [
  {
    id: 'M09_01',
    name: '聯立方程',
    description: '利用多項式解決聯立方程問題',
    category: 'simultaneous_equations',
    difficulty: 'intermediate'
  },
  {
    id: 'M09_02',
    name: '導數應用',
    description: '多項式函數的導數計算與應用',
    category: 'derivative_applications',
    difficulty: 'advanced'
  },
  {
    id: 'M09_03',
    name: '餘式定理證明',
    description: '餘式定理的數學證明與理論基礎',
    category: 'remainder_theorem_proof',
    difficulty: 'advanced'
  },
  {
    id: 'M09_04',
    name: '餘式定理應用',
    description: '餘式定理在多項式計算中的實際應用',
    category: 'remainder_theorem_application',
    difficulty: 'intermediate'
  },
  {
    id: 'M09_05',
    name: '餘式定理進階',
    description: '餘式定理的高級應用與複雜問題',
    category: 'advanced_remainder_theorem',
    difficulty: 'advanced'
  },
  {
    id: 'M09_06',
    name: '複數餘式定理',
    description: '餘式定理在複數域上的擴展',
    category: 'complex_remainder_theorem',
    difficulty: 'advanced'
  },
  {
    id: 'M09_07',
    name: '遞迴求解',
    description: '利用遞迴方法求解多項式問題',
    category: 'recursive_solving',
    difficulty: 'advanced'
  },
  {
    id: 'M09_08',
    name: '綜合除法',
    description: '掌握多項式的綜合除法技巧',
    category: 'synthetic_division',
    difficulty: 'intermediate'
  },
  {
    id: 'M09_09',
    name: '對稱多項式',
    description: '對稱多項式的性質與計算',
    category: 'symmetric_polynomials',
    difficulty: 'advanced'
  },
  {
    id: 'M09_10',
    name: '除法算法',
    description: '多項式除法的各種算法',
    category: 'division_algorithms',
    difficulty: 'intermediate'
  }
];

// M09 問題數據 - 主題 01-10
export const M09_PROBLEMS_01_TO_10 = [
  // M09-01: 聯立方程
  {
    id: 'M09_01_01',
    topicId: 'M09_01',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '利用多項式解聯立方程組：',
    parts: {
      a: {
        question: '解聯立方程組 $\\begin{cases} x + y = 5 \\\\ x^2 + y^2 = 13 \\end{cases}$',
        solution: {
          steps: [
            '由第一個方程得：$y = 5 - x$',
            '代入第二個方程：$x^2 + (5-x)^2 = 13$',
            '展開：$x^2 + 25 - 10x + x^2 = 13$，即 $2x^2 - 10x + 12 = 0$',
            '化簡：$x^2 - 5x + 6 = 0$，因式分解：$(x-2)(x-3) = 0$',
            '解得：$x = 2$ 或 $x = 3$，對應 $y = 3$ 或 $y = 2$'
          ],
          answer: '$(x,y) = (2,3)$ 或 $(3,2)$'
        },
        hint: '用代入法消元，將問題轉化為二次方程'
      },
      b: {
        question: '解聯立方程組 $\\begin{cases} xy = 6 \\\\ x^2 + y^2 = 37 \\end{cases}$',
        solution: {
          steps: [
            '設 $s = x + y$，$p = xy = 6$',
            '由恆等式 $(x+y)^2 = x^2 + 2xy + y^2$ 得：$s^2 = x^2 + y^2 + 2xy = 37 + 12 = 49$',
            '所以 $s = \\pm 7$，即 $x + y = 7$ 或 $x + y = -7$',
            '當 $x + y = 7$，$xy = 6$ 時，$x$、$y$ 是方程 $t^2 - 7t + 6 = 0$ 的根',
            '因式分解：$(t-1)(t-6) = 0$，得 $(x,y) = (1,6)$ 或 $(6,1)$',
            '當 $x + y = -7$，$xy = 6$ 時，$x$、$y$ 是方程 $t^2 + 7t + 6 = 0$ 的根',
            '因式分解：$(t+1)(t+6) = 0$，得 $(x,y) = (-1,-6)$ 或 $(-6,-1)$'
          ],
          answer: '$(x,y) = (1,6), (6,1), (-1,-6), (-6,-1)$'
        },
        hint: '利用韋達定理，設 $s = x + y$，$p = xy$'
      },
      c: {
        question: '解聯立方程組 $\\begin{cases} x^3 + y^3 = 35 \\\\ x + y = 5 \\end{cases}$',
        solution: {
          steps: [
            '利用立方和公式：$x^3 + y^3 = (x+y)(x^2 - xy + y^2)$',
            '由 $x + y = 5$ 得：$x^3 + y^3 = 5(x^2 - xy + y^2) = 35$',
            '所以 $x^2 - xy + y^2 = 7$',
            '又 $(x+y)^2 = x^2 + 2xy + y^2 = 25$，得 $x^2 + y^2 = 25 - 2xy$',
            '代入：$(25 - 2xy) - xy = 7$，即 $25 - 3xy = 7$，所以 $xy = 6$',
            '因此 $x$、$y$ 是方程 $t^2 - 5t + 6 = 0$ 的根，即 $(t-2)(t-3) = 0$'
          ],
          answer: '$(x,y) = (2,3)$ 或 $(3,2)$'
        },
        hint: '利用立方和公式和對稱多項式的關係'
      }
    }
  },
  {
    id: 'M09_01_02',
    topicId: 'M09_01',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '多項式聯立方程的參數問題：',
    parts: {
      a: {
        question: '已知聯立方程組 $\\begin{cases} x + y = a \\\\ x^2 + y^2 = b \\end{cases}$ 有實數解，求 $a$ 和 $b$ 的關係',
        solution: {
          steps: [
            '由第一個方程：$y = a - x$',
            '代入第二個方程：$x^2 + (a-x)^2 = b$',
            '展開：$x^2 + a^2 - 2ax + x^2 = b$，即 $2x^2 - 2ax + (a^2 - b) = 0$',
            '要有實數解，判別式 $\\Delta \\geq 0$：$(-2a)^2 - 4 \\cdot 2 \\cdot (a^2 - b) \\geq 0$',
            '化簡：$4a^2 - 8(a^2 - b) \\geq 0$，即 $4a^2 - 8a^2 + 8b \\geq 0$',
            '所以 $8b \\geq 4a^2$，即 $b \\geq \\frac{a^2}{2}$'
          ],
          answer: '$b \\geq \\frac{a^2}{2}$'
        },
        hint: '利用判別式分析實數解的存在條件'
      },
      b: {
        question: '當 $b = \\frac{a^2}{2}$ 時，求方程組的解',
        solution: {
          steps: [
            '此時判別式 $\\Delta = 0$，方程 $2x^2 - 2ax + (a^2 - b) = 0$ 有重根',
            '代入 $b = \\frac{a^2}{2}$：$2x^2 - 2ax + \\frac{a^2}{2} = 0$',
            '化簡：$x^2 - ax + \\frac{a^2}{4} = 0$，即 $(x - \\frac{a}{2})^2 = 0$',
            '所以 $x = \\frac{a}{2}$，對應 $y = a - \\frac{a}{2} = \\frac{a}{2}$'
          ],
          answer: '$(x,y) = (\\frac{a}{2}, \\frac{a}{2})$'
        },
        hint: '當判別式為零時，方程有重根'
      },
      c: {
        question: '若要使方程組有兩組不同的實數解，求 $a$ 和 $b$ 的關係',
        solution: {
          steps: [
            '要有兩組不同解，需要 $\\Delta > 0$',
            '即 $b > \\frac{a^2}{2}$',
            '此時二次方程 $2x^2 - 2ax + (a^2 - b) = 0$ 有兩個不同實根',
            '兩個 $x$ 值分別對應兩個不同的 $y$ 值，形成兩組解'
          ],
          answer: '$b > \\frac{a^2}{2}$'
        },
        hint: '兩組不同解對應判別式大於零的情況'
      }
    }
  },
  {
    id: 'M09_01_03',
    topicId: 'M09_01',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '高次多項式聯立方程：',
    parts: {
      a: {
        question: '解聯立方程組 $\\begin{cases} x^3 - y^3 = 7 \\\\ x - y = 1 \\end{cases}$',
        solution: {
          steps: [
            '利用立方差公式：$x^3 - y^3 = (x-y)(x^2 + xy + y^2)$',
            '代入 $x - y = 1$：$7 = 1 \\cdot (x^2 + xy + y^2)$',
            '所以 $x^2 + xy + y^2 = 7$',
            '由 $x = y + 1$ 代入：$(y+1)^2 + (y+1)y + y^2 = 7$',
            '展開：$y^2 + 2y + 1 + y^2 + y + y^2 = 7$',
            '化簡：$3y^2 + 3y - 6 = 0$，即 $y^2 + y - 2 = 0$',
            '因式分解：$(y+2)(y-1) = 0$，得 $y = -2$ 或 $y = 1$',
            '對應 $x = -1$ 或 $x = 2$'
          ],
          answer: '$(x,y) = (-1,-2)$ 或 $(2,1)$'
        },
        hint: '利用立方差公式分解，然後代入消元'
      },
      b: {
        question: '解聯立方程組 $\\begin{cases} x^4 - y^4 = 15 \\\\ x^2 - y^2 = 3 \\end{cases}$',
        solution: {
          steps: [
            '利用四次方差公式：$x^4 - y^4 = (x^2)^2 - (y^2)^2 = (x^2 - y^2)(x^2 + y^2)$',
            '代入已知條件：$15 = 3(x^2 + y^2)$',
            '所以 $x^2 + y^2 = 5$',
            '現在有 $\\begin{cases} x^2 - y^2 = 3 \\\\ x^2 + y^2 = 5 \\end{cases}$',
            '相加：$2x^2 = 8$，得 $x^2 = 4$，所以 $x = \\pm 2$',
            '相減：$2y^2 = 2$，得 $y^2 = 1$，所以 $y = \\pm 1$',
            '檢驗得四組解'
          ],
          answer: '$(x,y) = (2,1), (2,-1), (-2,1), (-2,-1)$'
        },
        hint: '利用四次方差公式，轉化為二次方程組'
      },
      c: {
        question: '解聯立方程組 $\\begin{cases} x^2 + xy + y^2 = 7 \\\\ x^2 - xy + y^2 = 3 \\end{cases}$',
        solution: {
          steps: [
            '兩式相加：$2(x^2 + y^2) = 10$，得 $x^2 + y^2 = 5$',
            '兩式相減：$2xy = 4$，得 $xy = 2$',
            '現在有 $\\begin{cases} x^2 + y^2 = 5 \\\\ xy = 2 \\end{cases}$',
            '設 $s = x + y$，則 $s^2 = (x+y)^2 = x^2 + 2xy + y^2 = 5 + 4 = 9$',
            '所以 $s = \\pm 3$，即 $x + y = 3$ 或 $x + y = -3$',
            '當 $x + y = 3$，$xy = 2$ 時，$x$、$y$ 是 $t^2 - 3t + 2 = 0$ 的根',
            '因式分解：$(t-1)(t-2) = 0$，得 $(x,y) = (1,2)$ 或 $(2,1)$',
            '當 $x + y = -3$，$xy = 2$ 時，$x$、$y$ 是 $t^2 + 3t + 2 = 0$ 的根',
            '因式分解：$(t+1)(t+2) = 0$，得 $(x,y) = (-1,-2)$ 或 $(-2,-1)$'
          ],
          answer: '$(x,y) = (1,2), (2,1), (-1,-2), (-2,-1)$'
        },
        hint: '利用加減消元法，結合韋達定理'
      }
    }
  },
  {
    id: 'M09_01_04',
    topicId: 'M09_01',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '三元聯立方程組：',
    parts: {
      a: {
        question: '解聯立方程組 $\\begin{cases} x + y + z = 6 \\\\ xy + yz + zx = 11 \\\\ xyz = 6 \\end{cases}$',
        solution: {
          steps: [
            '設 $x$、$y$、$z$ 是三次方程 $t^3 - at^2 + bt - c = 0$ 的三根',
            '由韋達定理：$a = x + y + z = 6$，$b = xy + yz + zx = 11$，$c = xyz = 6$',
            '所以三次方程為：$t^3 - 6t^2 + 11t - 6 = 0$',
            '嘗試整數根：$t = 1$ 時，$1 - 6 + 11 - 6 = 0$ ✓',
            '因式分解：$t^3 - 6t^2 + 11t - 6 = (t-1)(t^2 - 5t + 6)$',
            '繼續分解：$(t-1)(t-2)(t-3) = 0$',
            '所以根為 $t = 1, 2, 3$'
          ],
          answer: '$(x,y,z)$ 的所有排列：$(1,2,3)$、$(1,3,2)$、$(2,1,3)$、$(2,3,1)$、$(3,1,2)$、$(3,2,1)$'
        },
        hint: '利用韋達定理構造三次方程'
      },
      b: {
        question: '已知 $x + y + z = 0$，$x^2 + y^2 + z^2 = 14$，$xyz = 8$，求 $xy + yz + zx$ 的值',
        solution: {
          steps: [
            '利用恆等式：$(x + y + z)^2 = x^2 + y^2 + z^2 + 2(xy + yz + zx)$',
            '代入已知條件：$0^2 = 14 + 2(xy + yz + zx)$',
            '所以 $0 = 14 + 2(xy + yz + zx)$',
            '解得：$xy + yz + zx = -7$'
          ],
          answer: '$xy + yz + zx = -7$'
        },
        hint: '利用平方和與對稱多項式的關係'
      },
      c: {
        question: '承上題，求 $x$、$y$、$z$ 的具體值',
        solution: {
          steps: [
            '由前面得到：$x + y + z = 0$，$xy + yz + zx = -7$，$xyz = 8$',
            '構造三次方程：$t^3 - 0 \\cdot t^2 + (-7)t - 8 = 0$',
            '即 $t^3 - 7t - 8 = 0$',
            '嘗試整數根：$t = -1$ 時，$(-1)^3 - 7(-1) - 8 = -1 + 7 - 8 = -2 \\neq 0$',
            '$t = 2$ 時，$8 - 14 - 8 = -14 \\neq 0$',
            '$t = -2$ 時，$-8 - 7(-2) - 8 = -8 + 14 - 8 = -2 \\neq 0$',
            '$t = 4$ 時，$64 - 28 - 8 = 28 \\neq 0$',
            '使用其他方法或數值解法求得近似解'
          ],
          answer: '需要使用數值方法或進一步的代數技巧求解'
        },
        hint: '構造的三次方程可能需要更高級的求解技巧'
      }
    }
  },
  {
    id: 'M09_01_05',
    topicId: 'M09_01',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '聯立方程的幾何應用：',
    parts: {
      a: {
        question: '求過點 $(1,2)$ 且與拋物線 $y = x^2$ 相切的直線方程',
        solution: {
          steps: [
            '設切點為 $(a, a^2)$，切線斜率為 $2a$（拋物線導數）',
            '切線方程：$y - a^2 = 2a(x - a)$，即 $y = 2ax - a^2$',
            '切線過點 $(1,2)$：$2 = 2a(1) - a^2$',
            '整理：$a^2 - 2a + 2 = 0$',
            '判別式：$\\Delta = 4 - 8 = -4 < 0$，無實數解',
            '這表示過點 $(1,2)$ 無法作拋物線 $y = x^2$ 的切線'
          ],
          answer: '不存在這樣的切線'
        },
        hint: '利用切線的幾何性質和代數條件'
      },
      b: {
        question: '求過點 $(0,0)$ 且與拋物線 $y = x^2 - 2x + 2$ 相切的直線方程',
        solution: {
          steps: [
            '拋物線 $y = x^2 - 2x + 2$ 的導數為 $y\' = 2x - 2$',
            '設切點為 $(a, a^2 - 2a + 2)$，切線斜率為 $2a - 2$',
            '切線方程：$y - (a^2 - 2a + 2) = (2a - 2)(x - a)$',
            '化簡：$y = (2a - 2)x - a^2 + 2$',
            '切線過原點 $(0,0)$：$0 = 0 - a^2 + 2$',
            '所以 $a^2 = 2$，得 $a = \\pm\\sqrt{2}$',
            '對應兩條切線：斜率分別為 $2\\sqrt{2} - 2$ 和 $-2\\sqrt{2} - 2$'
          ],
          answer: '切線方程：$y = (2\\sqrt{2} - 2)x$ 和 $y = (-2\\sqrt{2} - 2)x$'
        },
        hint: '設切點坐標，利用導數求切線斜率'
      },
      c: {
        question: '求兩個拋物線 $y = x^2$ 和 $y = -x^2 + 4x - 3$ 的交點',
        solution: {
          steps: [
            '聯立方程：$\\begin{cases} y = x^2 \\\\ y = -x^2 + 4x - 3 \\end{cases}$',
            '消除 $y$：$x^2 = -x^2 + 4x - 3$',
            '整理：$2x^2 - 4x + 3 = 0$',
            '判別式：$\\Delta = 16 - 24 = -8 < 0$',
            '無實數解，表示兩拋物線不相交'
          ],
          answer: '兩拋物線無交點'
        },
        hint: '聯立方程求交點，注意判別式的意義'
      }
    }
  },

  // M09-02: 導數應用
  {
    id: 'M09_02_01',
    topicId: 'M09_02',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '多項式函數的導數計算：',
    parts: {
      a: {
        question: '求多項式 $P(x) = 3x^4 - 2x^3 + 5x^2 - 7x + 1$ 的導數',
        solution: {
          steps: [
            '使用冪函數求導法則：$(x^n)\' = nx^{n-1}$',
            '逐項求導：$(3x^4)\' = 12x^3$',
            '$(−2x^3)\' = −6x^2$，$(5x^2)\' = 10x$',
            '$(-7x)\' = -7$，$(1)\' = 0$',
            '合併：$P\'(x) = 12x^3 - 6x^2 + 10x - 7$'
          ],
          answer: '$P\'(x) = 12x^3 - 6x^2 + 10x - 7$'
        },
        hint: '冪函數的導數：$(x^n)\' = nx^{n-1}$'
      },
      b: {
        question: '求 $P(x)$ 在 $x = 1$ 處的切線方程',
        solution: {
          steps: [
            '計算 $P(1) = 3(1)^4 - 2(1)^3 + 5(1)^2 - 7(1) + 1 = 3 - 2 + 5 - 7 + 1 = 0$',
            '計算 $P\'(1) = 12(1)^3 - 6(1)^2 + 10(1) - 7 = 12 - 6 + 10 - 7 = 9$',
            '切點為 $(1, 0)$，切線斜率為 $9$',
            '切線方程：$y - 0 = 9(x - 1)$，即 $y = 9x - 9$'
          ],
          answer: '切線方程：$y = 9x - 9$'
        },
        hint: '切線方程：$y - y_0 = f\'(x_0)(x - x_0)$'
      },
      c: {
        question: '求 $P(x)$ 的極值點',
        solution: {
          steps: [
            '令 $P\'(x) = 0$：$12x^3 - 6x^2 + 10x - 7 = 0$',
            '這是三次方程，較難直接求解',
            '可以嘗試有理根定理或數值方法',
            '或者計算二階導數來分析：$P\'\'(x) = 36x^2 - 12x + 10$',
            '判別式：$\\Delta = 144 - 1440 = -1296 < 0$',
            '由於 $P\'\'(x) > 0$ 恆成立，$P\'(x)$ 單調遞增，最多一個零點'
          ],
          answer: '需要數值方法求解 $P\'(x) = 0$ 的根'
        },
        hint: '極值點是導數為零的點，可能需要數值方法'
      }
    }
  },
  {
    id: 'M09_02_02',
    topicId: 'M09_02',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '多項式函數的應用問題：',
    parts: {
      a: {
        question: '某物體的位移函數為 $s(t) = t^3 - 6t^2 + 9t + 2$，求速度函數和加速度函數',
        solution: {
          steps: [
            '速度是位移的導數：$v(t) = s\'(t)$',
            '計算：$v(t) = 3t^2 - 12t + 9$',
            '加速度是速度的導數：$a(t) = v\'(t)$',
            '計算：$a(t) = 6t - 12$'
          ],
          answer: '速度函數：$v(t) = 3t^2 - 12t + 9$，加速度函數：$a(t) = 6t - 12$'
        },
        hint: '速度是位移的導數，加速度是速度的導數'
      },
      b: {
        question: '求物體何時速度為零',
        solution: {
          steps: [
            '令 $v(t) = 0$：$3t^2 - 12t + 9 = 0$',
            '除以 $3$：$t^2 - 4t + 3 = 0$',
            '因式分解：$(t - 1)(t - 3) = 0$',
            '解得：$t = 1$ 或 $t = 3$'
          ],
          answer: '在 $t = 1$ 秒和 $t = 3$ 秒時速度為零'
        },
        hint: '速度為零時物體靜止或改變運動方向'
      },
      c: {
        question: '判斷 $t = 1$ 和 $t = 3$ 時物體的運動狀態',
        solution: {
          steps: [
            '檢查加速度：$a(1) = 6(1) - 12 = -6 < 0$',
            '在 $t = 1$ 時，速度為零且加速度為負，物體從正向運動轉為負向運動',
            '檢查加速度：$a(3) = 6(3) - 12 = 6 > 0$',
            '在 $t = 3$ 時，速度為零且加速度為正，物體從負向運動轉為正向運動',
            '也可以通過二階導數測試：$v\'(1) = -6 < 0$，$t = 1$ 是極大值點',
            '$v\'(3) = 6 > 0$，$t = 3$ 是極小值點'
          ],
          answer: '$t = 1$ 時物體達到最大速度後開始減速；$t = 3$ 時物體達到最小速度後開始加速'
        },
        hint: '結合速度和加速度的符號判斷運動狀態'
      }
    }
  },
  {
    id: 'M09_02_03',
    topicId: 'M09_02',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '多項式的高階導數：',
    parts: {
      a: {
        question: '求多項式 $f(x) = x^5 - 3x^4 + 2x^3 - x + 5$ 的三階導數',
        solution: {
          steps: [
            '一階導數：$f\'(x) = 5x^4 - 12x^3 + 6x^2 - 1$',
            '二階導數：$f\'\'(x) = 20x^3 - 36x^2 + 12x$',
            '三階導數：$f\'\'\'(x) = 60x^2 - 72x + 12$',
            '化簡：$f\'\'\'(x) = 12(5x^2 - 6x + 1)$'
          ],
          answer: '$f\'\'\'(x) = 60x^2 - 72x + 12$'
        },
        hint: '逐步對每一階導數求導'
      },
      b: {
        question: '求 $f\'\'\'(x) = 0$ 的解',
        solution: {
          steps: [
            '令 $60x^2 - 72x + 12 = 0$',
            '除以 $12$：$5x^2 - 6x + 1 = 0$',
            '使用公式法：$x = \\frac{6 \\pm \\sqrt{36 - 20}}{10} = \\frac{6 \\pm \\sqrt{16}}{10} = \\frac{6 \\pm 4}{10}$',
            '解得：$x = 1$ 或 $x = \\frac{1}{5}$'
          ],
          answer: '$x = 1$ 或 $x = \\frac{1}{5}$'
        },
        hint: '三階導數為零的點是函數的拐點'
      },
      c: {
        question: '分析這兩個點的幾何意義',
        solution: {
          steps: [
            '三階導數為零的點可能是拐點',
            '需要檢查四階導數：$f^{(4)}(x) = 120x - 72$',
            '在 $x = 1$：$f^{(4)}(1) = 120 - 72 = 48 \\neq 0$',
            '在 $x = \\frac{1}{5}$：$f^{(4)}(\\frac{1}{5}) = 24 - 72 = -48 \\neq 0$',
            '由於四階導數不為零，這兩點都是拐點',
            '在這些點處，函數的凹凸性發生變化'
          ],
          answer: '$x = 1$ 和 $x = \\frac{1}{5}$ 都是函數的拐點'
        },
        hint: '拐點是凹凸性改變的點，通常二階導數為零或不存在'
      }
    }
  },
  {
    id: 'M09_02_04',
    topicId: 'M09_02',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '多項式函數的優化問題：',
    parts: {
      a: {
        question: '用長度為 $60$ 米的圍欄圍成一個矩形，其中一邊靠牆不需要圍欄。設垂直於牆的邊長為 $x$ 米，建立面積關於 $x$ 的函數',
        solution: {
          steps: [
            '設垂直於牆的邊長為 $x$ 米，平行於牆的邊長為 $y$ 米',
            '圍欄約束：$2x + y = 60$，得 $y = 60 - 2x$',
            '面積函數：$A(x) = xy = x(60 - 2x) = 60x - 2x^2$',
            '定義域：$0 < x < 30$（確保 $y > 0$）'
          ],
          answer: '$A(x) = 60x - 2x^2$，$0 < x < 30$'
        },
        hint: '利用約束條件消除一個變量'
      },
      b: {
        question: '求使面積最大的 $x$ 值和最大面積',
        solution: {
          steps: [
            '對面積函數求導：$A\'(x) = 60 - 4x$',
            '令 $A\'(x) = 0$：$60 - 4x = 0$，得 $x = 15$',
            '檢查二階導數：$A\'\'(x) = -4 < 0$，確認是最大值',
            '最大面積：$A(15) = 60(15) - 2(15)^2 = 900 - 450 = 450$ 平方米',
            '對應的 $y = 60 - 2(15) = 30$ 米'
          ],
          answer: '$x = 15$ 米時面積最大，最大面積為 $450$ 平方米'
        },
        hint: '利用導數求極值，檢查二階導數確認極值性質'
      },
      c: {
        question: '若要使面積至少為 $400$ 平方米，求 $x$ 的取值範圍',
        solution: {
          steps: [
            '建立不等式：$A(x) \\geq 400$',
            '即 $60x - 2x^2 \\geq 400$',
            '整理：$2x^2 - 60x + 400 \\leq 0$',
            '除以 $2$：$x^2 - 30x + 200 \\leq 0$',
            '使用公式法求根：$x = \\frac{30 \\pm \\sqrt{900 - 800}}{2} = \\frac{30 \\pm 10}{2}$',
            '得根：$x = 20$ 或 $x = 10$',
            '由於拋物線開口向上，不等式解為 $10 \\leq x \\leq 20$'
          ],
          answer: '$x$ 的取值範圍為 $[10, 20]$'
        },
        hint: '解二次不等式確定函數值的約束範圍'
      }
    }
  },
  {
    id: 'M09_02_05',
    topicId: 'M09_02',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '多項式函數的圖像分析：',
    parts: {
      a: {
        question: '分析函數 $f(x) = x^3 - 3x^2 + 2$ 的單調性',
        solution: {
          steps: [
            '求導：$f\'(x) = 3x^2 - 6x = 3x(x - 2)$',
            '令 $f\'(x) = 0$：$3x(x - 2) = 0$，得 $x = 0$ 或 $x = 2$',
            '分析符號：當 $x < 0$ 時，$f\'(x) = 3x(x-2) > 0$（遞增）',
            '當 $0 < x < 2$ 時，$f\'(x) = 3x(x-2) < 0$（遞減）',
            '當 $x > 2$ 時，$f\'(x) = 3x(x-2) > 0$（遞增）'
          ],
          answer: '函數在 $(-\\infty, 0)$ 和 $(2, +\\infty)$ 上遞增，在 $(0, 2)$ 上遞減'
        },
        hint: '分析導數的符號確定單調性'
      },
      b: {
        question: '求函數的極值',
        solution: {
          steps: [
            '由單調性分析知：$x = 0$ 是極大值點，$x = 2$ 是極小值點',
            '極大值：$f(0) = 0^3 - 3(0)^2 + 2 = 2$',
            '極小值：$f(2) = 2^3 - 3(2)^2 + 2 = 8 - 12 + 2 = -2$',
            '驗證：$f\'\'(x) = 6x - 6$，$f\'\'(0) = -6 < 0$（極大值）',
            '$f\'\'(2) = 6 > 0$（極小值）'
          ],
          answer: '極大值：$f(0) = 2$；極小值：$f(2) = -2$'
        },
        hint: '極值在導數為零的點處取得'
      },
      c: {
        question: '求函數的拐點',
        solution: {
          steps: [
            '拐點是二階導數為零的點：$f\'\'(x) = 6x - 6 = 0$',
            '解得：$x = 1$',
            '拐點坐標：$(1, f(1)) = (1, 1 - 3 + 2) = (1, 0)$',
            '驗證：$f\'\'\'(x) = 6 \\neq 0$，確實是拐點',
            '在 $x = 1$ 處，函數從凹向下變為凹向上'
          ],
          answer: '拐點：$(1, 0)$'
        },
        hint: '拐點是凹凸性改變的點，二階導數為零'
      }
    }
  }

  // 繼續添加其他主題的問題...
];

// 導出模組
export default {
  topics: M09_TOPICS_01_TO_10,
  problems: M09_PROBLEMS_01_TO_10
}; 